import {CloseIcon} from '@chakra-ui/icons';
import {Flex, Heading, Skeleton, Stack, Text, useToast} from '@chakra-ui/react';
import _ from 'lodash';
import React, {useEffect, useRef, useState} from 'react';
import {
  MdDirectionsRun,
  MdLightbulbOutline,
  MdOutlinePanoramaPhotosphere,
  MdPinDrop,
  MdReportProblem,
} from 'react-icons/md';
import ButtonMinor from '../../../../components/ButtonMinor';
import {
  sendAssistantRequest,
  sendExploreSearch,
  sendLocationSave,
  sendNavTo,
  sendPanoLoad,
  sendReportSketch,
} from '../../../../vuplex';
import {PanoUnloadButton} from './PanoUnloadButton';
import useReverseGeocode from '../../../../hooks/useReverseGeocode';
import useStreetViewService from '../../../../hooks/useStreetViewService';
import {useMapsLibrary} from '@vis.gl/react-google-maps';

const ToastContent = ({
  altitude,
  brushStroke,
  isAltitudeDefined,
  latitude,
  longitude,
  onClose,
  placeId,
  skipReverseGeocode,
  streetViewLib,
  streetViewService,
  zoom,
}) => {
  const {cityCommaCountry, displayName} = useReverseGeocode(latitude, longitude, zoom,brushStroke || skipReverseGeocode);

  const {
    timeToDeletion,
    userId,
    userAlias,
  } = brushStroke || {};

  const [hasPano, setHasPano] = useState(false);
  const [hasPanoIsLoading, setHasPanoIsLoading] = useState(false);

  useEffect(() => {
    if (!streetViewService) return;
    setHasPanoIsLoading(true);
    streetViewService.getPanoramaByLocation({lat: latitude, lng: longitude})
      .then(res => setHasPano(true))
      .catch(err => setHasPano(false))
      .finally(() => setHasPanoIsLoading(false));
  }, [streetViewService]);

  return (
    <Flex
      alignItems="start"
      gap={3}
    >
      <PanoUnloadButton/>

      <Flex
        alignItems="flex-start"
        bg="#007AFF"
        borderRadius="2xl"
        color="white"
        gap={2}
        maxWidth="500px"
        p={2}
      >
        <Stack borderRight="solid 1px" borderColor="whiteAlpha.500">
          {!brushStroke && (
            <Heading
              color="white"
              noOfLines={3}
              size="md"
              textAlign="center"
              paddingX={2}
            >
              {displayName}
            </Heading>
          )}

          {brushStroke && (
            <>
              <Heading
                color="white"
                noOfLines={1}
                paddingX={2}
                size="md"
                textAlign="center"
              >
                {`Drawn by: ${userAlias}`}
              </Heading>
              <Heading
                color="white"
                paddingX={2}
                size="md"
                textAlign="center"
                w="full"
              >
                {timeToDeletion}
              </Heading>
            </>
          )}

          <Text color="whiteAlpha.600" textAlign="center">
            {latitude.toFixed(6)}, {longitude.toFixed(6)}
          </Text>

          <Flex justifyContent="center" gap={4} paddingTop={4}>
            <ButtonMinor
              onClick={() => {
                sendNavTo({
                  lat: latitude,
                  lon: longitude,
                  // zoom: map.getZoom() - zoomOffset,
                });
                // onClose();
              }}
              tooltip="Teleport to this location"
            >
              <MdDirectionsRun/>
              &nbsp;
              Go
            </ButtonMinor>

            {!brushStroke && (
              <ButtonMinor
                onClick={() => {
                  sendLocationSave({
                    address: displayName,
                    alt: altitude,
                    isAltDefined: isAltitudeDefined,
                    lat: latitude,
                    lon: longitude,
                    suggestedLabel: displayName,
                  });
                  onClose();
                }}
                tooltip="Save this location to Places"
              >
                <MdPinDrop/>
                &nbsp;
                Save
              </ButtonMinor>
            )}


            {!brushStroke && (
              <ButtonMinor
                isDisabled={!hasPano}
                isLoading={hasPanoIsLoading}
                onClick={() => {
                  if (!window.vuplex) { // TODO: proper module for Goood
                    const pano = new streetViewLib.StreetViewPanorama(
                      document.getElementById('pano'),
                      {
                        position: {
                          lat: latitude,
                          lng: longitude,
                        },
                      },
                    );
                    console.log(pano);
                    return;
                  }

                  sendPanoLoad({lat: latitude, lon: longitude});
                  onClose();
                }}
                tooltip={hasPano ? 'Load the nearest 360' : 'No 360 found nearby'}
              >
                <MdOutlinePanoramaPhotosphere/>
                &nbsp;
                360
              </ButtonMinor>
            )}

            {displayName && (
              <ButtonMinor
                bg="linear-gradient(234deg, rgba(188,221,252,1) 0%, rgba(179,244,249,1) 15%, rgba(158,193,219,1) 33%, rgba(249,181,193,1) 61%, rgba(249,193,227,1) 82%)"
                onClick={() => {
                  sendAssistantRequest({locationName: displayName});
                  onClose();
                }}
                tooltip="Ask Assistant for a Fun Fact"
              >
                <MdLightbulbOutline/>
                &nbsp;
                Fact
              </ButtonMinor>
            )}

            {brushStroke && (
              <ButtonMinor
                bg="darkred"
                color="white"
                onClick={() => {
                  onClose();
                  // close panel before taking a screenshot
                  _.delay(() => sendReportSketch({userId}), 500);
                }}>
                <MdReportProblem/>
                &nbsp;
                Report
              </ButtonMinor>
            )}

            {placeId && (
              <ButtonMinor
                onClick={() => {
                  sendExploreSearch({lat: latitude, lon: longitude, placeId});
                }}>
                View in Explore
              </ButtonMinor>
            )}

          </Flex>

          {/*<Skeleton isLoaded={!isLoading}>*/}
          {/*  <Box>*/}
          {/*    */}
          {/*  </Box>*/}
          {/*</Skeleton>*/}
        </Stack>

        <ButtonMinor bg="none" borderRadius={0} onClick={onClose}>
          <CloseIcon color="white"/>
        </ButtonMinor>
      </Flex>
    </Flex>
  );
};

const MinimapClickActionToast = ({
  altitude,
  brushStroke,
  isAltitudeDefined,
  isDisabled,
  latitude,
  longitude,
  placeId,
  onClose = _.noop,
  onClosedByUser = _.noop,
  skipReverseGeocode,
  zoom,
}) => {
  const streetView = useMapsLibrary('streetView');
  const streetViewService = useStreetViewService();
  const toast = useToast();
  const toastIdRef = useRef();

  useEffect(() => {
    const closeCurrentToast = () => toast.close(toastIdRef.current);

    if (!isDisabled && (placeId || latitude || longitude) && streetViewService) {
      toast.closeAll();

      toastIdRef.current = toast({
        onCloseComplete: onClose,
        render: () => (
          <ToastContent
            altitude={altitude}
            brushStroke={brushStroke}
            isAltitudeDefined={isAltitudeDefined}
            latitude={latitude}
            longitude={longitude}
            placeId={placeId}
            onClose={() => {
              closeCurrentToast();
              onClosedByUser();
            }}
            skipReverseGeocode={skipReverseGeocode}
            streetViewLib={streetView}
            streetViewService={streetViewService}
            zoom={zoom}
          />),
        duration: null,
        // icon: <MdVrpano color="#33bbff" size="1.5em"/>,
        isClosable: true,
        position: 'bottom',
      });
    } else {
      closeCurrentToast();
    }

    return closeCurrentToast;
  }, [
    brushStroke,
    isDisabled,
    latitude,
    longitude,
    onClose,
    placeId,
    streetViewService,
    zoom,
  ]);

  // useEffect(() => {
  //   if (panoId) {
  //     dispatch(pinsPanoIdSearchRequest({
  //       isNew: true,
  //       panoId,
  //     }));
  //   }
  // }, [panoId]);

  return null;
};

export default MinimapClickActionToast;
